import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUp, Info, RotateCcw, X } from "lucide-react";
import { LogoMark } from "../brand/Logo";
import { quickActions } from "../../lib/concierge";
import { useConcierge } from "../concierge/useConcierge";
import { Bubble, ContactCard, NoticeCard, SafetyCard, ServicePicker, ServicesCard, TrackCard } from "../concierge/Cards";
import { EnquiryResult, EnquiryReview } from "../concierge/EnquiryReview";

const Disclosure = ({ ai }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 text-xs leading-relaxed text-stone-300" data-testid="concierge-disclosure">
    <div className="flex gap-2">
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-orange-300" />
      {ai ? (
        <span>You're chatting with an <strong className="text-stone-100">AI assistant</strong>. Company facts come from TechCare's approved knowledge base; general guidance is marked. It can't see your account or enquiries. Messages are kept without contact details for up to 30 days, or clear them any time.</span>
      ) : (
        <span><strong className="text-stone-100">Guided mode.</strong> Choose an option below. AI answers are coming soon. Nothing is sent until you review and confirm.</span>
      )}
    </div>
    <p className="mt-2 border-t border-white/10 pt-2 text-stone-400" data-testid="concierge-privacy-guidance">
      Please never enter passwords, payment card or M-Pesa details, ID numbers or your private tracking code. We try to remove these automatically, but no filter is perfect. Privacy questions: hello@techcare.live.
    </p>
  </div>
);

const Item = ({ m, c, onClose }) => {
  if (m.role) return <Bubble m={m} onRetry={c.retry} />;
  switch (m.kind) {
    case "services": return <ServicesCard items={m.items} onClose={onClose} onQuote={(name) => c.startFlow("quote", { services: [name] })} />;
    case "picker": return <ServicePicker onDone={c.pickServices} />;
    case "track": return <TrackCard onClose={onClose} />;
    case "contact": return <ContactCard onMessage={() => c.startFlow("contact")} />;
    case "safety": return <SafetyCard text={m.text} />;
    case "notice": return <NoticeCard text={m.text} />;
    case "review": return <EnquiryReview draft={m.draft} startedAt={m.startedAt} onDone={(r) => c.resolveReview(m.id, r)} onCancel={() => c.resolveReview(m.id, null)} />;
    case "result": return <EnquiryResult {...m} />;
    case "cancelled": return <NoticeCard text="Draft discarded. Nothing was sent." />;
    default: return null;
  }
};

const Composer = ({ c }) => {
  const [text, setText] = useState("");
  const off = c.inputMode === "off" || c.busy;
  const placeholder = { off: "AI answers coming soon. Choose an option above", ai: "Ask about our services…", flow: "Type your answer…" }[c.inputMode];
  const submit = (e) => { e.preventDefault(); if (off) return; c.send(text); setText(""); };
  return (
    <form onSubmit={submit} className="flex items-center gap-2 border-t border-white/10 px-3 py-3">
      <input value={text} onChange={(e) => setText(e.target.value)} disabled={off} maxLength={c.inputMode === "ai" ? 800 : 2000}
        aria-label="Message the TechCare Concierge" placeholder={placeholder} data-testid="concierge-input"
        className="h-11 min-w-0 flex-1 rounded-full border border-white/10 bg-white/5 px-4 text-sm text-stone-100 outline-none placeholder:text-stone-500 focus:border-orange-500 disabled:cursor-not-allowed" />
      <button type="submit" disabled={off || !text.trim()} aria-label="Send message" data-testid="concierge-send-btn"
        className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-orange-500 text-black transition-[opacity,transform] hover:scale-105 disabled:opacity-30 disabled:hover:scale-100">
        <ArrowUp className="h-4 w-4" />
      </button>
    </form>
  );
};

const Panel = ({ c, onClose }) => {
  const end = useRef(null);
  const list = useRef(null);
  useEffect(() => {
    const last = c.messages[c.messages.length - 1];
    const node = list.current?.lastElementChild?.previousElementSibling;
    if (last && ["review", "services"].includes(last.kind) && node) node.scrollIntoView({ behavior: "smooth", block: "start" });
    else end.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [c.messages]);
  const ai = c.status.ai_enabled;
  return (
    <motion.div role="dialog" aria-modal="false" aria-labelledby="concierge-title" data-testid="concierge-panel"
      initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.98 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-x-2 bottom-[5.5rem] top-20 z-[90] flex flex-col overflow-hidden rounded-3xl border border-orange-500/20 bg-[#100D0C]/95 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8),0_0_0_1px_rgba(249,115,22,0.06)] backdrop-blur-xl sm:inset-x-auto sm:right-6 sm:top-auto sm:h-[min(680px,calc(100svh-8rem))] sm:w-[420px]" data-lenis-prevent>
      <div className="relative flex items-center justify-between overflow-hidden border-b border-white/10 px-4 py-3.5">
        <div aria-hidden="true" className="absolute -left-10 -top-16 h-40 w-40 rounded-full bg-orange-600/25 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <LogoMark className="h-8 w-auto" />
          <div>
            <p id="concierge-title" className="font-display text-sm text-stone-100">TechCare Concierge</p>
            <p className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] ${ai ? "text-emerald-300" : "text-amber-300"}`} data-testid="concierge-mode">
              <span className={`h-1.5 w-1.5 rounded-full ${ai ? "bg-emerald-400" : "bg-amber-400"}`} />{ai ? "AI assistant · Online" : c.status.limit_reached ? "Guided · AI paused" : "Guided · AI coming soon"}
            </p>
          </div>
        </div>
        <div className="relative flex items-center gap-1">
          <button onClick={c.reset} aria-label="Clear conversation" title="Clear conversation" data-testid="concierge-clear"
            className="grid h-8 w-8 place-items-center rounded-full text-stone-400 transition-colors hover:bg-white/10 hover:text-white"><RotateCcw className="h-4 w-4" /></button>
          <button onClick={onClose} aria-label="Close concierge" data-testid="concierge-close"
            className="grid h-8 w-8 place-items-center rounded-full text-stone-400 transition-colors hover:bg-white/10 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
      </div>
      <div ref={list} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" aria-live="polite" data-testid="concierge-messages">
        <p className="font-display text-lg leading-snug text-stone-50">Karibu. How can we take care of your tech today?</p>
        <Disclosure ai={ai} />
        {c.messages.map((m) => <Item key={m.id} m={m} c={c} onClose={onClose} />)}
        <div ref={end} />
      </div>
      <div className="flex gap-1.5 overflow-x-auto border-t border-white/10 px-3 pt-3 [scrollbar-width:none]" role="group" aria-label="Suggested actions">
        {quickActions.map((a) => (
          <button key={a.id} type="button" onClick={() => c.action(a.id)} disabled={c.busy} data-testid={`concierge-action-${a.id}`}
            className="shrink-0 rounded-full border border-orange-500/30 bg-orange-500/[0.06] px-3 py-1.5 text-xs text-orange-100 transition-[background-color,border-color] hover:border-orange-400 hover:bg-orange-500/15 disabled:opacity-50">{a.label}</button>
        ))}
      </div>
      <Composer c={c} />
    </motion.div>
  );
};

export const Concierge = () => {
  const [open, setOpen] = useState(false);
  const c = useConcierge();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  const ai = c.status.ai_enabled;
  return (
    <>
      <AnimatePresence>{open && <Panel c={c} onClose={() => setOpen(false)} />}</AnimatePresence>
      <button onClick={() => setOpen((o) => !o)} data-testid="concierge-trigger" aria-expanded={open}
        aria-label={open ? "Close TechCare Concierge" : "Open TechCare Concierge"}
        className="group fixed bottom-5 right-4 z-[90] flex h-14 items-center gap-3 rounded-full border border-orange-500/30 bg-[#141110]/90 pl-2 pr-5 shadow-[0_10px_40px_-10px_rgba(249,115,22,0.5)] backdrop-blur-xl transition-transform duration-300 hover:-translate-y-0.5 sm:right-6">
        <span className="relative grid h-10 w-10 place-items-center rounded-full bg-black/60 ring-1 ring-orange-500/40">
          <LogoMark className="h-5 w-auto" />
          <span className={`tc-pulse absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-[#141110] ${ai ? "bg-emerald-400" : "bg-amber-400"}`} />
        </span>
        <span className="text-left leading-tight">
          <span className="block text-[13px] font-semibold text-stone-100">Concierge</span>
          <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-stone-400">{ai ? "Ask us anything" : "How can we help?"}</span>
        </span>
      </button>
    </>
  );
};
